import { createSlice, isPending, isRejected } from '@reduxjs/toolkit';

import { RootState } from '../..';
import {
  createCourseStudent,
  deleteCourseStudent,
  getCoursesByUserAsAdmin,
  getCourseStudentsAsAdmin,
  updateCourseStudent as adminUpdateCourseStudent,
} from './adminApi';
import { IUser } from '../../UserSlice';
import { ICourse } from '../CoursesSlice';
import {
  getCourseStudentsByCourseId,
  getCourseStudentsByUser,
  getCoursesByUser,
  updateCourseStudent,
  updateCourseStudentLearningDetails,
} from './api';

export const COURSE_STUDENT_PRICING_TYPES = {
  ONETIME: 'ONETIME',
  SUBSCRIPTION: 'SUBSCRIPTION',
  CREDIT: 'CREDIT',
  TRIAL: 'TRIAL',
} as const;

export type TCourseStudentStatus = 'ENABLE' | 'DISABLE';
export type TCourseStudentPricingType = typeof COURSE_STUDENT_PRICING_TYPES[keyof typeof COURSE_STUDENT_PRICING_TYPES];

export interface ICourseStudentLearningDetails {
  subjectExperienceLevel?: string;
  learningStyle?: string;
  upcomingExamStatus?: string;
  otherRequirement?: string;
  // new
  lastExamStatus?: string;
  currentGrade?: string;
  targetGrade?: string;
  upcomingExamDate?: string;
  specialRequirement?: string;
  specificTopic?: string;
}

export interface ICourseStudent {
  _id: string;
  status: 'ENABLE' | 'DISABLE';
  startDate: string;
  endDate: string;
  createdAt: string;
  updatedAt: string;
  student: IUser;
  course: ICourse;
  pricingType?: TCourseStudentPricingType;
  credits?: number;
  learningDetails?: ICourseStudentLearningDetails;
  autoTopupHours?: number;
  autoTopupEnabled?: boolean;
}

export interface ICourseStudentForSubscriptions {
  course: ICourse;
  student: string;
  startDate?: string;
  endDate?: string;
  status: TCourseStudentStatus;
  groupClassStudentId?: string;
  classes?: { total: number; completed: number };
  createdAt?: string;
  credits?: number;
}

export interface ICoursesForSubscriptions {
  activeCourses: Array<ICourseStudentForSubscriptions>;
  completedCourses: Array<ICourseStudentForSubscriptions>;
}

export interface IInitialCourseStudentsState {
  status: 'idle' | 'loading' | 'rejected';
  // Admin
  adminCourseStudentsData?: {
    limit?: number;
    offset?: number;
    totalDocs?: number;
    list: Array<ICourseStudent>;
  };
  // User
  subscriptions: ICoursesForSubscriptions;
  courseStudents: Array<ICourseStudent>;
}

const initialState: IInitialCourseStudentsState = {
  status: 'idle',
  // Admin
  adminCourseStudentsData: undefined,
  // User
  subscriptions: {
    activeCourses: [],
    completedCourses: [],
  },
  courseStudents: [],
};

export const CourseStudentsSlice = createSlice({
  name: 'courses',
  initialState,
  reducers: {
    resetAdminCourseStudentsData: (state) => {
      state.adminCourseStudentsData = undefined;
    },
    resetCourseStudents: (state) => {
      state.courseStudents = [];
    },
  },
  extraReducers: (builder) => {
    builder
      // Admin
      // Get course students
      .addCase(getCourseStudentsAsAdmin.fulfilled, (state, action) => {
        state.status = 'idle';

        const result = action.payload;
        const newAdminCourseStudentData = {
          limit: result.limit,
          offset: result.offset,
          totalDocs: result.totalDocs || 0,
          list: result.docs || [],
        };

        state.adminCourseStudentsData = newAdminCourseStudentData;
      })
      // Create course students
      .addCase(createCourseStudent.fulfilled, (state, action) => {
        state.status = 'idle';

        const result = action.payload;

        if (state.adminCourseStudentsData) {
          const newAdminCourseStudentsData = {
            ...state.adminCourseStudentsData,
          };

          newAdminCourseStudentsData.list.unshift(result);

          if (newAdminCourseStudentsData.totalDocs) {
            newAdminCourseStudentsData.totalDocs += 1;
          } else {
            newAdminCourseStudentsData.totalDocs = 1;
          }

          state.adminCourseStudentsData = newAdminCourseStudentsData;
        }
      })
      // Delete course student
      .addCase(deleteCourseStudent.fulfilled, (state, action) => {
        state.status = 'idle';

        const result = action.payload;

        if (state.adminCourseStudentsData) {
          const newAdminCourseStudentsData = {
            ...state.adminCourseStudentsData,
          };

          newAdminCourseStudentsData.list = newAdminCourseStudentsData.list.filter(
            (item) => item._id !== result._id
          );

          if (newAdminCourseStudentsData.totalDocs) {
            newAdminCourseStudentsData.totalDocs -= 1;
          }

          state.adminCourseStudentsData = newAdminCourseStudentsData;
        }
      })
      // Update course tutor
      .addCase(adminUpdateCourseStudent.fulfilled, (state, action) => {
        state.status = 'idle';

        const result = action.payload;

        if (state.adminCourseStudentsData) {
          const newAdminCourseStudentsData = {
            ...state.adminCourseStudentsData,
          };

          const index = newAdminCourseStudentsData.list.findIndex(
            (item) => item._id === result._id
          );

          if (index > -1) {
            newAdminCourseStudentsData.list[index] = result;
          }

          state.adminCourseStudentsData = newAdminCourseStudentsData;
        }
      })
      .addCase(getCoursesByUserAsAdmin.fulfilled, (state, action) => {
        state.status = 'idle';
        state.subscriptions = action.payload;
      })

      // User
      .addCase(getCoursesByUser.fulfilled, (state, action) => {
        state.status = 'idle';
        state.subscriptions = action.payload;
      })
      .addCase(getCourseStudentsByCourseId.fulfilled, (state, action) => {
        state.status = 'idle';
        state.courseStudents = action.payload;
      })
      .addCase(getCourseStudentsByUser.fulfilled, (state, action) => {
        state.status = 'idle';
        state.courseStudents = action.payload;
      })

      // Update course student learning details
      .addCase(
        updateCourseStudentLearningDetails.fulfilled,
        (state, action) => {
          state.status = 'idle';

          const result = action.payload;
          const newCourseStudents = [...state.courseStudents];

          if (result && newCourseStudents && newCourseStudents.length) {
            const index = newCourseStudents.findIndex(
              (item) => item._id === result._id
            );

            if (index > -1) {
              newCourseStudents[index] = {
                ...newCourseStudents[index],
                learningDetails: result.learningDetails,
              };
            }
          }

          state.courseStudents = [...newCourseStudents];
        }
      )
      .addCase(updateCourseStudent.fulfilled, (state, action) => {
        state.status = 'idle';
      });

    // Matcher
    builder
      .addMatcher(
        isPending(
          getCourseStudentsAsAdmin,
          createCourseStudent,
          deleteCourseStudent,
          adminUpdateCourseStudent,
          getCoursesByUser,
          getCourseStudentsByCourseId,
          getCourseStudentsByUser,
          updateCourseStudentLearningDetails,
          updateCourseStudent
        ),
        (state) => {
          state.status = 'loading';
        }
      )
      .addMatcher(
        isRejected(
          getCourseStudentsAsAdmin,
          createCourseStudent,
          deleteCourseStudent,
          adminUpdateCourseStudent,
          getCoursesByUser,
          getCourseStudentsByCourseId,
          getCourseStudentsByUser,
          updateCourseStudentLearningDetails,
          updateCourseStudent
        ),
        (state) => {
          state.status = 'rejected';
        }
      );
  },
});

export const {
  resetAdminCourseStudentsData,
  resetCourseStudents,
} = CourseStudentsSlice.actions;

export const getCourseStudentsState = (
  state: RootState
): RootState['courseStudents'] => state.courseStudents;

export * from './adminApi';
export default CourseStudentsSlice.reducer;
